<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-people me-2"></i>Customers Management
      </h4>
      <div v-if="profile.role !== 'user'">
        <button class="btn btn-primary" @click="addCustomer">
          <i class="bi bi-person-plus me-1"></i>Add Customer
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading customers...</p>
      </div>

      <!-- Customers Content -->
      <div v-if="!loading" class="customers-content fade-in">
        <!-- Count and search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ customers.total }}</span>
            <span class="text-muted">Customers</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search customer" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>PHONE</th>
                <th>ADDRESS</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(customer, index) in customers.data" :key="customer.id" class="customer-row" :class="{'bg-light': index % 2 === 0}">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="supplier-avatar me-2" :class="`bg-${['primary','success','info','warning'][index % 4]}-light`">
                      <span class="avatar-text">{{ customer.name.charAt(0).toUpperCase() }}</span>
                    </div>
                    <span class="fw-medium">{{ customer.name }}</span>
                  </div>
                </td>
                <td>{{ customer.email }}</td>
                <td>{{ customer.phone }}</td>
                <td>{{ customer.address }}</td>
                <td class="text-end">
                  <div class="btn-group">
                    <button class="btn btn-sm btn-outline-primary" @click="customerDetails(customer.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                    <button v-if="profile.role !== 'user'" class="btn btn-sm btn-outline-secondary ms-1" @click="editCustomer(customer.id)">
                      <i class="bi bi-pencil"></i> Edit
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination v-model="dataTablePage" :pages="totalPages" :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import customerService from "@/api/customerService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";

export default {
  name: "Customers",
  components: { Pagination },
  data(){
    return {
      profile: {},
      loading: true,
      customers: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
    }
  },
  methods:{
    async getCustomers(){
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      try {
        this.customers = await customerService.getCustomers(params);
      } catch (e){
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getCustomers()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getCustomers()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    customerDetails(id){ router.push('/customer/' + id) },
    addCustomer(){ router.push('/add-customer') },
    editCustomer(id){ router.push('/edit-customer/' + id) },
  },
  computed:{
    totalPages(){
      return this.customers?.last_page || 1
    }
  },
  watch:{
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getCustomers()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getCustomers()
    },
  },
  async created(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getCustomers();
  }
}
</script>

<style scoped>
.customers-content { animation: fadeIn 0.3s ease-in-out; }
@keyframes fadeIn{ from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

/* Table & Row styles copied from Suppliers */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.customer-row {
  transition: all 0.2s ease;
}

.customer-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Avatar styles */
.supplier-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

.avatar-text {
  color: inherit;
}
</style>
